import React from 'react';
import { Map, MapPin, Globe } from 'lucide-react';

/**
 * Компонент для отображения карты часовых поясов с фокусом на России
 */
const TimeZoneMap = ({ selectedCity }) => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-6 mb-8">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
        <Map className="w-5 h-5 mr-2 text-green-500" />
        Карта часовых поясов России
      </h2>

      <div className="relative">
        {/* Карта часовых поясов России в формате SVG */}
        <div className="max-w-full overflow-x-auto py-2">
          <svg
            viewBox="0 0 1000 500"
            className="w-full h-auto dark:filter dark:brightness-90"
            aria-label="Карта часовых поясов России"
          >
            {/* Контуры России с обозначением часовых поясов */}
            <g stroke="currentColor" fill="#e5e7eb" className="dark:fill-gray-700">
              {/* Калининградская область (UTC+2) */}
              <path d="M197,203 L205,196 L214,199 L220,208 L215,217 L205,220 L195,214 L192,207 z" 
                className="fill-indigo-100 dark:fill-indigo-900/40 stroke-indigo-400" />
              
              {/* Европейская часть России (UTC+3) */}
              <path d="M205,140 L340,150 L370,190 L375,290 L330,350 L270,365 L250,330 L220,320 L200,250 L210,220 L220,210 L200,195 z"
                className="fill-purple-100 dark:fill-purple-900/40 stroke-purple-400" />
              
              {/* Урал (UTC+5) */}
              <path d="M340,150 L400,130 L435,190 L440,230 L430,280 L380,330 L330,350 L370,190 z"
                className="fill-blue-100 dark:fill-blue-900/40 stroke-blue-400" />
              
              {/* Западная Сибирь (UTC+7) */}
              <path d="M400,130 L500,120 L560,160 L565,210 L550,260 L500,300 L430,280 L440,230 L435,190 z"
                className="fill-cyan-100 dark:fill-cyan-900/40 stroke-cyan-400" />
              
              {/* Восточная Сибирь (UTC+8) */}
              <path d="M500,120 L590,110 L630,150 L640,200 L625,240 L585,270 L550,260 L565,210 L560,160 z"
                className="fill-teal-100 dark:fill-teal-900/40 stroke-teal-400" />
              
              {/* Якутия (UTC+9) */}
              <path d="M590,110 L680,100 L720,140 L730,190 L715,230 L675,260 L625,240 L640,200 L630,150 z"
                className="fill-green-100 dark:fill-green-900/40 stroke-green-400" />
              
              {/* Дальний Восток (UTC+10) */}
              <path d="M680,100 L760,90 L800,130 L810,170 L800,210 L760,240 L715,230 L730,190 L720,140 z"
                className="fill-lime-100 dark:fill-lime-900/40 stroke-lime-400" />
              
              {/* Камчатка и Магадан (UTC+11 и UTC+12) */}
              <path d="M760,90 L830,85 L870,130 L875,170 L850,210 L800,230 L750,250 L760,240 L800,210 L810,170 L800,130 z"
                className="fill-yellow-100 dark:fill-yellow-900/40 stroke-yellow-400" />
            </g>

            {/* Метки часовых поясов */}
            <g className="text-xs font-medium">
              <text x="195" y="190" className="fill-indigo-700 dark:fill-indigo-300">UTC+2</text>
              <text x="270" y="250" className="fill-purple-700 dark:fill-purple-300">UTC+3</text>
              <text x="390" y="230" className="fill-blue-700 dark:fill-blue-300">UTC+5</text>
              <text x="490" y="210" className="fill-cyan-700 dark:fill-cyan-300">UTC+7</text>
              <text x="570" y="190" className="fill-teal-700 dark:fill-teal-300">UTC+8</text>
              <text x="660" y="170" className="fill-green-700 dark:fill-green-300">UTC+9</text>
              <text x="750" y="160" className="fill-lime-700 dark:fill-lime-300">UTC+10</text>
              <text x="815" y="150" className="fill-yellow-700 dark:fill-yellow-300">UTC+11/12</text>
            </g>

            {/* Названия основных городов */}
            <g className="text-xs">
              <text x="200" y="208" className="fill-gray-700 dark:fill-gray-300">Калининград</text>
              <text x="240" y="215" className="fill-gray-700 dark:fill-gray-300">Москва</text>
              <text x="385" y="220" className="fill-gray-700 dark:fill-gray-300">Екатеринбург</text>
              <text x="490" y="230" className="fill-gray-700 dark:fill-gray-300">Новосибирск</text>
              <text x="570" y="220" className="fill-gray-700 dark:fill-gray-300">Иркутск</text>
              <text x="650" y="200" className="fill-gray-700 dark:fill-gray-300">Якутск</text>
              <text x="740" y="190" className="fill-gray-700 dark:fill-gray-300">Владивосток</text>
              <text x="820" y="180" className="fill-gray-700 dark:fill-gray-300">Камчатка</text>
            </g>

            {/* Маркер выбранного города, если он есть */}
            {selectedCity && (
              <g className="animate-pulse">
                <circle 
                  cx={getCityCoordinatesForMap(selectedCity).x} 
                  cy={getCityCoordinatesForMap(selectedCity).y} 
                  r="8" 
                  className="fill-red-500"
                />
                <text 
                  x={getCityCoordinatesForMap(selectedCity).x + 12} 
                  y={getCityCoordinatesForMap(selectedCity).y + 5} 
                  className="fill-red-600 dark:fill-red-400 font-bold"
                >
                  {selectedCity.name}
                </text>
              </g>
            )}
          </svg>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg text-sm text-green-700 dark:text-green-300">
          <div className="flex items-start space-x-2">
            <MapPin className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-medium mb-1">Часовые пояса России</h3>
              <p className="text-sm text-green-600 dark:text-green-400">
                Россия занимает 11 часовых поясов от UTC+2 до UTC+12, 
                что делает её самой протяженной страной по часовым поясам в мире.
              </p>
            </div>
          </div>
        </div>

        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-sm text-blue-700 dark:text-blue-300">
          <div className="flex items-start space-x-2">
            <Globe className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-medium mb-1">Историческая справка</h3>
              <p className="text-sm text-blue-600 dark:text-blue-400">
                До 2010 года в России было 11 часовых поясов, затем их сократили до 9, 
                а в 2014 году снова увеличили до 11, добавив две временные зоны.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Вспомогательная функция для получения примерных координат городов на карте
const getCityCoordinatesForMap = (city) => {
  // Этот словарь задает приблизительные координаты на SVG для основных городов
  const cityCoordinates = {
    'Moscow': { x: 240, y: 205 },
    'Saint_Petersburg': { x: 230, y: 180 },
    'Kaliningrad': { x: 200, y: 200 },
    'Murmansk': { x: 250, y: 150 },
    'Kazan': { x: 300, y: 220 },
    'Yekaterinburg': { x: 385, y: 210 },
    'Novosibirsk': { x: 490, y: 220 },
    'Krasnoyarsk': { x: 525, y: 235 },
    'Irkutsk': { x: 570, y: 210 },
    'Yakutsk': { x: 650, y: 190 },
    'Vladivostok': { x: 740, y: 180 },
    'Kamchatka': { x: 820, y: 170 },
    // Города за пределами России расположены вне карты, используем условную точку
    'default': { x: 500, y: 450 }
  };

  return cityCoordinates[city.id] || cityCoordinates['default'];
};

export default TimeZoneMap;